import {
  FetchArgs, BaseQueryApi, fetchBaseQuery, retry,
} from '@reduxjs/toolkit/query';
import { ClientLog } from 'src/constants/clientLog';
import ClientLogger from 'src/utils/logger';
import { publishApiDurationMetric } from 'src/metrics/apiMetrics';
import { logAPIRequestLatency } from 'src/utils/latencyLogger';
import { getMetaTagContent } from 'src/utils/metaTags/core';
import { META_TAG } from 'src/constants/metaTag';
import { HTTP_HEADER } from 'src/constants/httpHeader';
import { redirectToSignIn } from 'src/utils/authRedirect';

const HTTP_STATUS_PRECONDITION_FAILED = 412;

/**
 * Wait for CSRF token to become available in DOM meta tags
 * @param maxWaitMs Maximum time to wait in milliseconds
 * @returns Promise resolving to readiness status and token
 */
const waitForCSRFToken = (maxWaitMs = 500): Promise<{
  isReady: boolean;
  csrfToken?: string;
}> => {
  return new Promise((resolve) => {
    const startTime = Date.now();

    const checkToken = (): void => {
      const csrfToken = getMetaTagContent(META_TAG.CSRF_TOKEN);

      if (csrfToken) {
        resolve({ isReady: true, csrfToken });
        return;
      }

      // Check if timeout reached
      if (Date.now() - startTime >= maxWaitMs) {
        const finalToken = getMetaTagContent(META_TAG.CSRF_TOKEN);
        resolve({
          isReady: false,
          csrfToken: finalToken || undefined,
        });
        return;
      }

      // Check again after 50ms
      setTimeout(checkToken, 50);
    };

    checkToken();
  });
};

/**
 * Base query with CSRF protection for state-modifying requests
 */
const baseQueryWithCSRF = fetchBaseQuery({
  // Explicit baseUrl prevents credential-in-URL errors when document.baseURI
  // contains HTTP Basic Auth userinfo (e.g., amazon-QBusiness bot accessing
  // sell.amazon.com). window.location.origin never includes userinfo.
  baseUrl: window.location.origin,
  prepareHeaders: (headers: Headers, api) => {
    // Create new Headers object to avoid mutating the parameter (ESLint no-param-reassign)
    const newHeaders = new Headers(headers);
    newHeaders.set('Content-Type', 'application/json');

    // Only add CSRF token for state-modifying requests (mutations)
    if (api.type === 'mutation') {
      const csrfToken = getMetaTagContent(META_TAG.CSRF_TOKEN);
      if (csrfToken) {
        newHeaders.set(HTTP_HEADER.CSRF_TOKEN, csrfToken);
      }
    }

    return newHeaders;
  },
});

export const enhancedBaseQuery = async (
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions: {},
) => {
  // Record start time for API duration tracking
  const startTime = performance.now();

  // Wait for CSRF token readiness for mutations to prevent 403 errors
  if (api.type === 'mutation') {
    const { isReady, csrfToken } = await waitForCSRFToken();

    if (!isReady) {
      ClientLogger.warn(ClientLog.APIClientError, new Error('MONS configuration not ready with CSRF Tokens'), {
        endpoint: typeof args === 'string' ? args : args.url,
        csrfTokenAvailable: !!csrfToken,
        url: window.location.href,
        waitTimeMs: 500,
        readinessCheck: 'csrf_timeout',
      });
    }
  }

  const result = await baseQueryWithCSRF(args, api, extraOptions);

  // Track duration for both success and error cases
  const duration = performance.now() - startTime;
  if (api.endpoint && typeof api.endpoint === 'string') {
    publishApiDurationMetric(api.endpoint, duration, !!result.error);
    logAPIRequestLatency(api.endpoint, duration, !!result.error);
  }

  if (result.error) {
    // Get request details
    const { url = args, method = 'GET' } = typeof args === 'string' ? { url: args } : args;
    const { error } = result;
    const errorStatusCode = Number(error.status);
    const errorMessage = typeof error.data === 'string'
      ? error.data
      : (() => {
        try {
          return JSON.stringify(error.data) ?? 'Unknown error';
        } catch {
          return 'Error data could not be serialized';
        }
      })();
    const errorData = new Error(errorMessage);

    const logContext = {
      url,
      method,
      error,
      status: error.status,
      currentUrl: window.location.href,
    };

    // Generic failure logging as warn
    // We have detailed level logging per status code below
    ClientLogger.warn(ClientLog.APIRequestFailure, errorData, logContext);

    if (errorStatusCode >= 500) {
      // Handle 5xx errors
      switch (errorStatusCode) {
        case 502:
        case 503:
        case 504:
          ClientLogger.warn(ClientLog.APITransientError, errorData, logContext);
          break;
        default:
          ClientLogger.error(ClientLog.APICriticalError, errorData, logContext);
          // Bail out of retries
          retry.fail(result.error, result.meta);
      }
    } else if (errorStatusCode >= 400) {
      // Handle 4xx errors
      switch (errorStatusCode) {
        case 400:
          ClientLogger.warn(ClientLog.APIBadRequestError, errorData, logContext);
          break;
        case HTTP_STATUS_PRECONDITION_FAILED:
          // 412 = gated user whose auth expired. SUOS detected clientId=CAMPUS
          // but Mons headers lack customerId/merchantId.
          // Redirect to Mons sign-in with redirect_to back to current page.
          // Log is inside redirectToSignIn so duplicate 412s don't produce duplicate logs.
          redirectToSignIn({
            message: 'API 412: user authentication expired. Redirecting to sign-in.',
            url: window.location.href,
            endpoint: typeof args === 'string' ? args : (args as FetchArgs).url,
          });
          break;
        case 429:
          ClientLogger.warn(ClientLog.APIThrottlingError, errorData, logContext);
          break;
        default:
          ClientLogger.warn(ClientLog.APIClientError, errorData, logContext);
      }
      // We should never retry 4xx errors as it indicates client-side problems
      // that won't be resolved by simply retrying.
      retry.fail(result.error, result.meta);
    }
  }

  return result;
};
